import { ActionTree } from 'vuex'

import api from '@/apiClient'

import { Order, OrderFilters, OrderPageQuery } from '@/models'

import { assertToken, mergeById } from '@/utils'

import { RootState } from '../../state'
import { OrderingState } from '../state'

import {
  ADD_INVOICE_ORDERS,
  ADD_ORDER,
  APPLY_ORDER_FILTERS,
  INCREMENT_ORDERS_QUERY_INDEX,
  LOADED_ALL_INVOICE_ORDERS,
  LOADED_ALL_ORDERS,
  RESET_LOADED_ALL_INVOICE_ORDERS,
  RESET_LOADED_ALL_ORDERS,
  RESET_ORDERS_QUERY_INDEX,
  SET_ORDERS_DEFAULT_FILTERS_FLAG,
  UPDATE_INVOICE_ORDERS,
  UPDATE_INVOICE_ORDERS_TOTAL,
  UPDATE_ORDERS,
  UPDATE_ORDERS_TOTAL
} from './mutations'

const actions: ActionTree<OrderingState, RootState> = {
  /**
   * Adds a new Order and updates an Invoice relation.
   *
   * It adds the Invoice relation if it is not found.
   *
   * @param order Order to be added.
   */
  addOrder ({ commit, state }, order: Order) {
    const invoiceId = order.invoiceId

    let invoiceOrders = state.invoices.orders[invoiceId]

    if (invoiceOrders === undefined) {
      commit(ADD_INVOICE_ORDERS, invoiceId)
      invoiceOrders = state.invoices.orders[invoiceId]
    }

    commit(ADD_ORDER, order)

    commit(UPDATE_INVOICE_ORDERS_TOTAL, {
      invoiceId,
      total: invoiceOrders.total + 1
    })

    commit(UPDATE_INVOICE_ORDERS, {
      invoiceId,
      orders: [...invoiceOrders.ids, order.id]
    })
  },

  /**
   * Applies OrderFilters to the query and resets the loading state.
   *
   * @param filters OrderFilters to be applied.
   * @param isDefaultFilters Flags if `filters` is default filters.
   */
  changeOrderFilter (
    { commit },
    { filters, isDefaultFilters }: { filters: OrderFilters; isDefaultFilters: boolean }
  ) {
    commit(APPLY_ORDER_FILTERS, filters)
    commit(SET_ORDERS_DEFAULT_FILTERS_FLAG, isDefaultFilters)

    commit(RESET_LOADED_ALL_ORDERS)
    commit(RESET_ORDERS_QUERY_INDEX)
  },

  /**
   * Loads the first Order in an Invoice.
   *
   * It rejects if the user is not logged in.
   *
   * It updates the Invoice relation.
   *
   * @param invoiceId Invoice id whose first Order will be loaded.
   */
  async loadFirstOrder (
    { commit, dispatch, getters, rootState, rootGetters, state },
    invoiceId: number
  ) {
    const businessUnit = rootState.businessUnit
    const providerId: number | null = rootGetters['auth/currentProvider']

    if (providerId === null) {
      throw new Error('User is not authenticated')
    }

    if (businessUnit === null) {
      throw new Error('BusinessUnit is not loaded')
    }

    const tokenV2 = assertToken(rootState.auth.tokenV2)

    const page = await api.orders.fetchOrderPage(
      tokenV2,
      providerId,
      businessUnit.id,
      {
        length: 1,
        invoiceId
      }
    )

    if (page.elements.length > 0) {
      const order = page.elements[0]

      const invoiceOrders = state.invoices.orders[invoiceId]

      if (invoiceOrders === undefined) {
        throw new Error(`Orders relation for Invoice "${invoiceId}" was not found`)
      }

      if (getters.orderById(order.id) === null) {
        commit(UPDATE_ORDERS_TOTAL, state.orders.total + 1)
      }

      if (!invoiceOrders.ids.has(order.id)) {
        await dispatch('addOrder', order)
      } else {
        const orders = mergeById(state.orders.elements, [order])
        commit(UPDATE_ORDERS, orders)
      }
    }
  },

  /**
   * Loads one Order.
   *
   * It rejects if the user is not logged in.
   *
   * It loads the Invoice if it is not loaded.
   *
   * @param id Order id to be loaded.
   */
  async loadOrder ({ commit, dispatch, getters, rootState, rootGetters, state }, id: number) {
    const businessUnit = rootState.businessUnit
    const providerId: number | null = rootGetters['auth/currentProvider']

    if (providerId === null) {
      throw new Error('User is not authenticated')
    }

    if (businessUnit === null) {
      throw new Error('BusinessUnit is not loaded')
    }

    const tokenV2 = assertToken(rootState.auth.tokenV2)

    const order = await api.orders.fetchOrder(tokenV2, providerId, businessUnit.id, id)

    let invoiceOrders = state.invoices.orders[order.invoiceId]

    if (invoiceOrders === undefined) {
      await dispatch('loadInvoice', order.invoiceId)
      invoiceOrders = state.invoices.orders[order.invoiceId]
    }

    if (getters.orderById(id) === null) {
      commit(UPDATE_ORDERS_TOTAL, state.orders.total + 1)
    }

    if (!invoiceOrders.ids.has(order.id)) {
      await dispatch('addOrder', order)
    } else {
      const orders = mergeById(state.orders.elements, [order])
      commit(UPDATE_ORDERS, orders)
    }
  },

  /**
   * Loads a page of Orders.
   *
   * It rejects if the user is not logged in.
   *
   * It loads missing Invoices.
   *
   * It updates the Invoice relation for each loaded Order.
   *
   * When a previously loaded page changes, it desynchronizes and will load
   * an empty page after the last page, which will reset the query index.
   */
  async loadOrderPage ({ commit, dispatch, getters, rootState, rootGetters, state }) {
    const businessUnit = rootState.businessUnit
    const providerId: number | null = rootGetters['auth/currentProvider']

    if (providerId === null) {
      throw new Error('User is not authenticated')
    }

    if (businessUnit === null) {
      throw new Error('BusinessUnit is not loaded')
    }

    const tokenV2 = assertToken(rootState.auth.tokenV2)

    const page = await api.orders.fetchOrderPage(
      tokenV2,
      providerId,
      businessUnit.id,
      state.orders.query
    )

    if (state.orders.total !== page.total) {
      commit(UPDATE_ORDERS_TOTAL, page.total)
    }

    if (page.elements.length > 0) {
      const newInvoices = page.elements
        .map((order) => order.invoiceId)
        .filter((invoiceId) => getters.invoiceById(invoiceId) === null)

      if (newInvoices.length > 0) {
        await dispatch('loadInvoiceList', newInvoices)
      }

      const orders = mergeById(state.orders.elements, page.elements)

      commit(UPDATE_ORDERS, orders)

      const invoicesOrders = page.elements.reduce((map, order) => {
        let ids = map.get(order.invoiceId)

        if (ids === undefined) {
          ids = []
          map.set(order.invoiceId, ids)
        }

        ids.push(order.id)
        return map
      }, new Map<number, number[]>())

      for (const [invoiceId, ids] of invoicesOrders) {
        commit(UPDATE_INVOICE_ORDERS, {
          invoiceId,
          orders: [
            ...state.invoices.orders[invoiceId].ids,
            ...ids
          ]
        })
      }

      if (getters.filteredOrders.length === page.total) {
        commit(LOADED_ALL_ORDERS)
      }

      commit(INCREMENT_ORDERS_QUERY_INDEX, page.elements.length)
    } else {
      commit(RESET_ORDERS_QUERY_INDEX)

      if (page.total === 0 && state.orders.elements.length > 0) {
        commit(UPDATE_ORDERS, [])
      }
    }
  },

  /**
   * Loads all Orders pages for an Invoice.
   *
   * It rejects if the user is not logged in.
   *
   * It loads the Invoice if it is not loaded.
   *
   * @param invoiceId Invoice id whose Orders will be loaded.
   */
  async loadInvoiceOrders (
    { commit, dispatch, rootState, rootGetters, state },
    invoiceId: number
  ) {
    const businessUnit = rootState.businessUnit
    const providerId: number | null = rootGetters['auth/currentProvider']

    if (providerId === null) {
      throw new Error('User is not authenticated')
    }

    if (businessUnit === null) {
      throw new Error('BusinessUnit is not loaded')
    }

    let invoiceOrders = state.invoices.orders[invoiceId]

    if (invoiceOrders === undefined) {
      await dispatch('loadInvoice', invoiceId)
      invoiceOrders = state.invoices.orders[invoiceId]
    }

    const query: OrderPageQuery = {
      index: 0,
      invoiceId
    }

    let result: number[] = []
    let count = 0
    let total = invoiceOrders.total
    let didSkip = false

    do {
      // Get Token every time to not load more Orders
      // if the user logs out during the process.
      const tokenV2 = assertToken(rootState.auth.tokenV2)

      const page = await api.orders.fetchOrderPage(
        tokenV2,
        providerId,
        businessUnit.id,
        query
      )

      if (total !== page.total && (page.total > 0 || count === 0)) {
        total = page.total
        commit(UPDATE_INVOICE_ORDERS_TOTAL, { invoiceId, total })

        if (total === 0) {
          commit(UPDATE_INVOICE_ORDERS, { invoiceId, orders: [] })
        }
      }

      if (page.elements.length > 0) {
        const orders = mergeById(state.orders.elements, page.elements)

        commit(UPDATE_ORDERS, orders)

        result = [...result, ...page.elements.map((i) => i.id)]

        commit(UPDATE_INVOICE_ORDERS, { invoiceId, orders: result })

        count += page.elements.length
      } else {
        didSkip = true
        commit(RESET_LOADED_ALL_INVOICE_ORDERS, invoiceId)
        // If an Order is added in a previously loaded page, the count
        // would desynchronize and an empty page would be loaded at
        // the end. It avoids an infinite loop.
        break
      }

      query.index = count
    } while (count < total)

    if (!didSkip && total > 0) {
      commit(LOADED_ALL_INVOICE_ORDERS, invoiceId)
    }
  },

  /**
   * Cancels an Order.
   *
   * It rejects if the user is not logged in.
   *
   * @param id Order id to be canceled.
   */
  async cancelOrder ({ dispatch, rootGetters, rootState }, id: number) {
    const businessUnit = rootState.businessUnit
    const providerId: number | null = rootGetters['auth/currentProvider']

    if (providerId === null) {
      throw new Error('User is not authenticated')
    }

    if (businessUnit === null) {
      throw new Error('BusinessUnit is not loaded')
    }

    const tokenV2 = assertToken(rootState.auth.tokenV2)

    await api.orders.cancelOrder(tokenV2, providerId, businessUnit.id, id)

    if (!rootGetters.isWebSocketConnected) {
      await dispatch('loadOrder', id)
    }
  },

  /**
   * Completes an Order.
   *
   * It rejects if the user is not logged in.
   *
   * @param id Order id to be completed.
   */
  async completeOrder ({ dispatch, rootGetters, rootState }, id: number) {
    const businessUnit = rootState.businessUnit
    const providerId: number | null = rootGetters['auth/currentProvider']

    if (providerId === null) {
      throw new Error('User is not authenticated')
    }

    if (businessUnit === null) {
      throw new Error('BusinessUnit is not loaded')
    }

    const tokenV2 = assertToken(rootState.auth.tokenV2)

    await api.orders.completeOrder(tokenV2, providerId, businessUnit.id, id)

    if (!rootGetters.isWebSocketConnected) {
      await dispatch('loadOrder', id)
    }
  },

  /**
   * Starts an Order.
   *
   * It rejects if the user is not logged in.
   *
   * @param id Order id to be started.
   */
  async startOrder ({ dispatch, rootGetters, rootState }, id: number) {
    const businessUnit = rootState.businessUnit
    const providerId: number | null = rootGetters['auth/currentProvider']

    if (providerId === null) {
      throw new Error('User is not authenticated')
    }

    if (businessUnit === null) {
      throw new Error('BusinessUnit is not loaded')
    }

    const tokenV2 = assertToken(rootState.auth.tokenV2)

    await api.orders.startOrder(tokenV2, providerId, businessUnit.id, id)

    if (!rootGetters.isWebSocketConnected) {
      await dispatch('loadOrder', id)
    }
  }
}

export default actions
